import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Checkbox,
} from "@mui/material";
import ZoneGroupIcon from "./ZoneGroupIcon";

// Styles found in deviceControlStyles.js

const ControlCard = ({
  zone,
  language,
  robotStatus,
  t,
  getButtonStatus,
  isButtonDisabled,
  handleButtonClick,
  getButtonBackgroundColor,
  buttonStyles,
  selectionMode = false,
  isSelected = false,
  selectionOrder = null,
  onToggleSelect,
  forceDisabled = false,
}) => {
  const handleCardClick = () => {
    if (!selectionMode || !onToggleSelect) return;
    onToggleSelect(zone.id);
  };

  const buttonDisabled = forceDisabled || isButtonDisabled(zone);

  return (
    <Grid item key={zone.id}>
      <Card
        onClick={handleCardClick}
        sx={{
          height: 184,
          width: 184,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          cursor: selectionMode ? "pointer" : "default",
          border: isSelected ? "2px solid #1976d2" : "2px solid transparent",
          boxShadow: isSelected ? 6 : 1,
          transform: isSelected ? "scale(1.01)" : "scale(1)",
          transition: "all 0.2s ease",
          ...(selectionMode
            ? {
                animation:
                  "zoneSelectionShake 0.5s ease-in-out infinite alternate",
                "@keyframes zoneSelectionShake": {
                  "0%": { transform: "rotate(-0.6deg)" },
                  "100%": { transform: "rotate(0.6deg)" },
                },
              }
            : {}),
        }}
      >
        {(selectionMode || isSelected) && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: 999,
              px: 0.5,
              py: 0.25,
            }}
          >
            {selectionMode ? (
              <Checkbox checked={isSelected} size="small" sx={{ p: 0.25 }} />
            ) : null}
            {isSelected && (
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                {selectionOrder}
              </Box>
            )}
          </Box>
        )}

        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <ZoneGroupIcon
              groupName={zone.nameEn}
              displayName={zone.name}
              size={18}
              sx={{ mr: 1 }}
            />
          </Box>

          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{
              fontSize: "0.92rem",
              fontWeight: "bold",
              textAlign: "center",
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            {zone.name
              ? language === "en"
                ? zone.nameEn.toUpperCase()
                : zone.name.toUpperCase()
              : "UNKNOWN zone"}
          </Typography>

          {selectionMode ? (
            <Button variant="outlined" fullWidth disabled sx={{ opacity: 0.9 }}>
              {isSelected
                ? t("buttonStatus.selected").toUpperCase()
                : t("buttonStatus.tapSelect").toUpperCase()}
            </Button>
          ) : getButtonStatus(zone) === "paused" ? (
            // Show both Stop and Resume buttons when paused
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                disabled={buttonDisabled}
                onClick={() => handleButtonClick(zone.id, zone.name, "stop")}
                sx={{
                  ...buttonStyles.base,
                  backgroundColor: buttonDisabled
                    ? "#9e9e9e"
                    : getButtonBackgroundColor("stop"),
                  "&:hover": {
                    backgroundColor: buttonDisabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor("stop"),
                    ...buttonStyles.hover,
                  },
                }}
              >
                {t("buttonStatus.stop")}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                disabled={buttonDisabled}
                onClick={() => handleButtonClick(zone.id, zone.name, "resume")}
                sx={{
                  ...buttonStyles.base,
                  backgroundColor: buttonDisabled
                    ? "#9e9e9e"
                    : getButtonBackgroundColor("resume"),
                  "&:hover": {
                    backgroundColor: buttonDisabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor("resume"),
                    ...buttonStyles.hover,
                  },
                }}
              >
                {t("buttonStatus.resume")}
              </Button>
            </Box>
          ) : (
            // Show single button for all other statuses
            <Button
              variant="outlined"
              fullWidth
              disabled={buttonDisabled}
              onClick={() =>
                handleButtonClick(zone.id, zone.name, getButtonStatus(zone))
              }
              sx={{
                ...buttonStyles.base,
                backgroundColor: buttonDisabled
                  ? "#9e9e9e"
                  : getButtonBackgroundColor(getButtonStatus(zone)),
                "&:hover": {
                  backgroundColor: buttonDisabled
                    ? "#9e9e9e"
                    : getButtonBackgroundColor(getButtonStatus(zone)),
                  ...buttonStyles.hover,
                },
              }}
            >
              {t(`buttonStatus.${getButtonStatus(zone)}`)}
            </Button>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ControlCard;
